import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useEvent } from '@/hooks/useEvent'
import { supabase } from '@/lib/supabaseClient'
import type { CompanyRow, HotelRow, PortalStatus, RoomTypeRow } from '@/lib/types'
import { Badge, Button, Card, EmptyState, Input, OccupancyBadge, PageSpinner, Select, useToast } from '@/components/ui'
import { ExportRow, buildRoomingListExport, fetchRows } from '@/lib/excel/exportBuilder'
import { roomOccupancyStatusFromCounts } from '@/lib/domain/roomLogic'

interface HotelSummaryRow {
  hotel_id: string
  hotel_name: string
  room_type_id: string
  room_type_name: string
  allocated_rooms: number
  guests: number
  companies_count: number
  inventory_total: number | null
}

/** Event-wide, filterable, searchable rooming list across every company and hotel (section 8/76) -- also the hotel inventory / overallocation view (sections 78-80). */
export default function RoomingList() {
  const { event } = useEvent()
  const navigate = useNavigate()
  const { push } = useToast()

  const [companies, setCompanies] = useState<CompanyRow[]>([])
  const [hotels, setHotels] = useState<HotelRow[]>([])
  const [roomTypes, setRoomTypes] = useState<RoomTypeRow[]>([])
  const [hotelSummaries, setHotelSummaries] = useState<HotelSummaryRow[]>([])
  const [rows, setRows] = useState<ExportRow[] | null>(null)

  const [companyId, setCompanyId] = useState('')
  const [hotelId, setHotelId] = useState('')
  const [roomTypeId, setRoomTypeId] = useState('')
  const [portalStatus, setPortalStatus] = useState('')
  const [search, setSearch] = useState('')
  const [exporting, setExporting] = useState(false)

  useEffect(() => {
    (async () => {
      const [{ data: c }, { data: h }, { data: rt }, { data: hs }] = await Promise.all([
        supabase.from('companies').select('*').eq('event_id', event.id).is('archived_at', null).order('name'),
        supabase.from('hotels').select('*').eq('event_id', event.id).order('name'),
        supabase.from('room_types').select('*').eq('event_id', event.id).order('name'),
        supabase.rpc('admin_hotel_summaries', { p_event_id: event.id }),
      ])
      setCompanies((c as CompanyRow[]) || [])
      setHotels((h as HotelRow[]) || [])
      setRoomTypes((rt as RoomTypeRow[]) || [])
      setHotelSummaries((hs as HotelSummaryRow[]) || [])
    })()
  }, [event.id])

  async function load() {
    setRows(null)
    const data = await fetchRows({
      eventId: event.id,
      companyId: companyId || null,
      hotelId: hotelId || null,
      roomTypeId: roomTypeId || null,
      portalStatus: (portalStatus as PortalStatus) || null,
    })
    setRows(data)
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event.id, companyId, hotelId, roomTypeId, portalStatus])

  const filteredRows = useMemo(() => {
    if (!rows) return []
    const q = search.trim().toLowerCase()
    if (!q) return rows
    return rows.filter((r) => {
      if (r.company_name.toLowerCase().includes(q) || r.hotel_name.toLowerCase().includes(q) || r.display_label.toLowerCase().includes(q)) return true
      return (r.guests || []).some((g) => (g.full_name || '').toLowerCase().includes(q) || (g.mobile || '').includes(q))
    })
  }, [rows, search])

  async function exportView() {
    setExporting(true)
    try {
      await buildRoomingListExport(
        { eventId: event.id, companyId: companyId || null, hotelId: hotelId || null, roomTypeId: roomTypeId || null, portalStatus: (portalStatus as PortalStatus) || null },
        event.name,
        'Rooming_List'
      )
    } catch (e: any) {
      push(e.message || 'Export failed', 'error')
    } finally {
      setExporting(false)
    }
  }

  return (
    <div className="mx-auto max-w-6xl px-6 py-8">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold text-slate-900">Rooming List</h1>
          <p className="text-sm text-slate-500">Every room across every company and hotel, in one place.</p>
        </div>
        <Button variant="secondary" loading={exporting} onClick={exportView}>
          Export This View (.xlsx)
        </Button>
      </div>

      {hotelSummaries.length > 0 && (
        <Card className="mb-6 overflow-x-auto">
          <div className="border-b border-slate-200 px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-slate-500">Hotel Inventory</div>
          <table className="w-full min-w-[720px] text-sm">
            <thead className="bg-slate-50 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-2">Hotel</th>
                <th className="px-4 py-2">Room Type</th>
                <th className="px-4 py-2 text-right">Allocated Rooms</th>
                <th className="px-4 py-2 text-right">Inventory Limit</th>
                <th className="px-4 py-2 text-right">Guests</th>
                <th className="px-4 py-2 text-right">Companies</th>
                <th className="px-4 py-2">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {hotelSummaries.map((s) => {
                const over = s.inventory_total != null && s.allocated_rooms > s.inventory_total
                return (
                  <tr key={`${s.hotel_id}-${s.room_type_id}`} className="hover:bg-slate-50">
                    <td className="px-4 py-2">{s.hotel_name}</td>
                    <td className="px-4 py-2">{s.room_type_name}</td>
                    <td className="px-4 py-2 text-right">{s.allocated_rooms}</td>
                    <td className="px-4 py-2 text-right text-slate-500">{s.inventory_total ?? '—'}</td>
                    <td className="px-4 py-2 text-right">{s.guests}</td>
                    <td className="px-4 py-2 text-right">{s.companies_count}</td>
                    <td className="px-4 py-2">
                      {over ? (
                        <Badge tone="red">⚠ Over inventory limit</Badge>
                      ) : s.inventory_total != null ? (
                        <Badge tone="green">✓ Within limit</Badge>
                      ) : (
                        <Badge tone="slate">No limit set</Badge>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </Card>
      )}

      <Card className="mb-4 flex flex-wrap items-end gap-3 p-4">
        <div className="min-w-[180px] flex-1">
          <label className="mb-1 block text-xs font-medium text-slate-600">Search</label>
          <Input placeholder="Company, hotel, room, guest name or mobile…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-slate-600">Company</label>
          <Select value={companyId} onChange={(e) => setCompanyId(e.target.value)} className="min-w-[160px]">
            <option value="">All companies</option>
            {companies.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-slate-600">Hotel</label>
          <Select value={hotelId} onChange={(e) => setHotelId(e.target.value)} className="min-w-[160px]">
            <option value="">All hotels</option>
            {hotels.map((h) => (
              <option key={h.id} value={h.id}>
                {h.name}
              </option>
            ))}
          </Select>
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-slate-600">Room Type</label>
          <Select value={roomTypeId} onChange={(e) => setRoomTypeId(e.target.value)} className="min-w-[140px]">
            <option value="">All room types</option>
            {roomTypes.map((rt) => (
              <option key={rt.id} value={rt.id}>
                {rt.name}
              </option>
            ))}
          </Select>
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-slate-600">Portal Status</label>
          <Select value={portalStatus} onChange={(e) => setPortalStatus(e.target.value)} className="min-w-[140px]">
            <option value="">Any status</option>
            <option value="draft">Draft</option>
            <option value="open">Open</option>
            <option value="edit_only">Edit Only</option>
            <option value="closed">Closed</option>
          </Select>
        </div>
      </Card>

      {rows === null ? (
        <PageSpinner label="Loading rooms…" />
      ) : filteredRows.length === 0 ? (
        <EmptyState title="No rooms match these filters" />
      ) : (
        <Card className="overflow-x-auto">
          <table className="w-full min-w-[900px] text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-2.5">Company</th>
                <th className="px-4 py-2.5">Hotel</th>
                <th className="px-4 py-2.5">Room Type</th>
                <th className="px-4 py-2.5">Room</th>
                <th className="px-4 py-2.5">Guests</th>
                <th className="px-4 py-2.5">Occupancy</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredRows.map((r) => (
                <tr key={r.room_id} className="cursor-pointer hover:bg-slate-50" onClick={() => navigate(`/admin/events/${event.id}/companies/${r.company_id}`)}>
                  <td className="px-4 py-2.5 font-medium text-slate-900">{r.company_name}</td>
                  <td className="px-4 py-2.5">{r.hotel_name}</td>
                  <td className="px-4 py-2.5">{r.room_type_name}</td>
                  <td className="px-4 py-2.5 text-slate-600">{r.display_label}</td>
                  <td className="px-4 py-2.5 text-slate-600">
                    {(r.guests || []).filter((g) => g.full_name).length > 0
                      ? (r.guests || [])
                          .filter((g) => g.full_name)
                          .map((g) => g.full_name)
                          .join(', ')
                      : '—'}
                  </td>
                  <td className="px-4 py-2.5">
                    <OccupancyBadge status={roomOccupancyStatusFromCounts(r.guests_filled, r.capacity)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  )
}
